/**
 * BenchmarkDetails — fuller "here are all the numbers" card for the About page.
 *
 * Renders the full BENCHMARK_SUMMARY: headline metrics table, per-query
 * nDCG@5 breakdown, regression floor, links to the baseline report and the
 * raw dataset on GitHub, plus a brief methodology paragraph so a skeptical
 * reader can audit the claim without leaving the page.
 */

import { CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";

import { BENCHMARK_SUMMARY } from "@/data/benchmark";

function pct(n: number): string {
  return (n * 100).toFixed(1);
}

export function BenchmarkDetails() {
  const m = BENCHMARK_SUMMARY.metrics;
  const headroom = m.ndcgAt5 - BENCHMARK_SUMMARY.floor;
  const generatedDate = new Date(BENCHMARK_SUMMARY.generatedAt).toLocaleDateString(
    "en-US",
    { year: "numeric", month: "long", day: "numeric" },
  );

  return (
    <div className="ta-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-emerald-400" />
        <h3 className="text-base font-semibold text-slate-100">
          Benchmark {BENCHMARK_SUMMARY.version}
        </h3>
        <span className="text-xs text-slate-500 ml-auto">
          Generated {generatedDate}
        </span>
      </div>

      <p className="text-sm text-slate-400 leading-relaxed mb-5">
        The scoring engine is regression-gated against a labeled dataset of{" "}
        <strong className="text-slate-200">
          {BENCHMARK_SUMMARY.matchCount} resume/JD pairs
        </strong>{" "}
        across{" "}
        <strong className="text-slate-200">
          {BENCHMARK_SUMMARY.queryCount} hiring scenarios
        </strong>
        . Every push to <code className="text-slate-300">main</code> re-runs
        the benchmark in GitHub Actions. If nDCG@5 drops below{" "}
        <strong className="text-slate-200">{pct(BENCHMARK_SUMMARY.floor)}%</strong>
        , the build fails.
      </p>

      {/* Headline grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Metric
          label="nDCG@5"
          value={`${pct(m.ndcgAt5)}%`}
          tone="emerald"
          primary
        />
        <Metric
          label="Precision@1"
          value={`${pct(m.precisionAt1)}%`}
          tone="emerald"
        />
        <Metric label="MRR" value={m.mrr.toFixed(3)} tone="emerald" />
        <Metric
          label="Spearman ρ"
          value={m.spearman.toFixed(3)}
          tone="slate"
        />
      </div>

      {/* Per-query table */}
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
        Per-query nDCG@5 — all {BENCHMARK_SUMMARY.perQuery.length} queries ≥ 95%
      </div>
      <div className="rounded-lg border border-slate-800/60 overflow-hidden mb-5">
        <ul className="divide-y divide-slate-800/60">
          {BENCHMARK_SUMMARY.perQuery.map((q) => (
            <li
              key={q.id}
              className="flex items-center gap-2 px-3 py-2 text-xs"
            >
              <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
              <span className="flex-1 text-slate-300 truncate" title={q.label}>
                {q.label}
              </span>
              <span className="text-slate-100 font-mono font-semibold w-16 text-right">
                {pct(q.ndcgAt5)}%
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Floor callout */}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 mb-5">
        <div className="text-xs text-slate-400 leading-relaxed">
          <strong className="text-emerald-300">
            {pct(headroom)} percentage points of headroom
          </strong>{" "}
          above the CI regression floor ({pct(BENCHMARK_SUMMARY.floor)}% nDCG@5).
          Every commit to the engine is measured.
        </div>
      </div>

      {/* Links */}
      <div className="flex flex-wrap gap-3 text-xs">
        <a
          href={BENCHMARK_SUMMARY.baselineReportUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-slate-700 hover:bg-slate-900 text-slate-300 transition"
        >
          Full baseline report
          <ExternalLink className="w-3 h-3" />
        </a>
        <a
          href={BENCHMARK_SUMMARY.datasetUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-slate-700 hover:bg-slate-900 text-slate-300 transition"
        >
          Raw dataset
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Methodology note */}
      <p className="mt-5 text-[11px] text-slate-600 leading-relaxed">
        Methodology: the CI benchmark uses a deterministic hashed bag-of-words
        embedder for speed and reproducibility. Production (browser) uses{" "}
        <code className="text-slate-500">@xenova/transformers</code> MiniLM,
        which produces strictly higher absolute scores but the same relative
        ranking. Ranking metrics measured here are representative of what
        real users see; absolute calibration is better in production.
      </p>
    </div>
  );
}

interface MetricProps {
  label: string;
  value: string;
  tone: "emerald" | "slate";
  primary?: boolean;
}

function Metric({ label, value, tone, primary = false }: MetricProps) {
  const toneClass =
    tone === "emerald"
      ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
      : "text-slate-300 border-slate-800/60 bg-slate-900/40";

  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {label}
      </div>
      <div
        className={`font-bold ${primary ? "text-2xl" : "text-xl"} leading-tight`}
      >
        {value}
      </div>
    </div>
  );
}
